import React from 'react'
import { Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import RoleDropdown from './RoleDropdown'
import AllocationCell from './AllocationCell'
import type { MemberRecord } from '../types'

type Props = {
  member: MemberRecord
  isMe: boolean
  isLast: boolean
  myRole: 'owner' | 'admin' | 'member'
  canAllocate: boolean
  allocationError?: string
  onRoleChange: (membershipId: string, newRole: 'admin' | 'member') => void
  onAllocationSave: (membershipId: string, allocation: number | null) => Promise<void>
  onRequestRemove: (member: MemberRecord) => void
}

const MemberRow: React.FC<Props> = ({
  member: m,
  isMe,
  isLast,
  myRole,
  canAllocate,
  allocationError,
  onRoleChange,
  onAllocationSave,
  onRequestRemove,
}) => {
  const { currentTheme: t } = useTheme()
  const joinDate = new Date(m.joinedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

  return (
    <div
      className="flex items-center px-[18px] py-3"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${t.borderSecondary}`,
        backgroundColor: isMe ? 'rgba(79,70,229,0.05)' : 'transparent',
      }}
    >
      <div className="flex-1 flex items-center gap-3 min-w-0">
        <div
          className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0"
          style={{ background: t.accentGradient }}
        >
          {m.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold leading-tight flex items-center gap-1.5" style={{ color: t.text }}>
            {m.name}
            {isMe && (
              <span
                className="text-[10.5px] font-semibold px-[7px] py-[1px] rounded-full"
                style={{ backgroundColor: 'rgba(79,70,229,0.08)', color: t.accent }}
              >
                you
              </span>
            )}
          </p>
          <p className="text-[11.5px] mt-0.5 truncate" style={{ color: t.mutedText }}>{m.email}</p>
        </div>
      </div>
      <div className="w-20 flex justify-center">
        <RoleDropdown
          current={m.role}
          membershipId={m.id}
          myRole={myRole}
          isMe={isMe}
          onChanged={onRoleChange}
        />
      </div>
      <div className="hidden md:flex w-[150px] justify-end">
        {m.role === 'owner' ? (
          <span className="text-[12.5px] font-medium" style={{ color: t.mutedText }}>Unlimited</span>
        ) : (
          <AllocationCell
            membershipId={m.id}
            memberName={m.name}
            allocation={m.aiCreditAllocation}
            used={m.aiCreditsUsedThisPeriod}
            canEdit={canAllocate}
            onSave={onAllocationSave}
            {...(allocationError ? { error: allocationError } : {})}
          />
        )}
      </div>
      <div className="hidden sm:block w-[88px] text-right">
        <span className="text-xs" style={{ color: t.mutedText }}>{joinDate}</span>
      </div>
      <div className="w-8 flex justify-end">
        {!isMe && m.role !== 'owner' && (
          <button
            onClick={() => onRequestRemove(m)}
            className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center transition-colors"
            style={{ color: t.mutedText }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = t.errorBackground
              e.currentTarget.style.color = t.errorText
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = t.mutedText
            }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  )
}

export default React.memo(MemberRow)
